'use client';

import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink, FileText } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import type { ConsolidationArticle } from './ConsolidationArticleCard.types';
import type { CitingRegulation } from '@/lib/enabling-clause';
import type { ConsolidationArticleResponse } from '@justisio/shared';
import { DiffSideBySide } from '@/components/ui/DiffText';
import { LegalTextFormatter } from '@/components/ui/LegalTextFormatter';
import { cn } from '@/lib/utils';

export const changeTypeConfig: Record<
  string,
  {
    label: string;
    borderColor: string;
    badgeColor: string;
    accentColor: string;
  }
> = {
  unchanged: {
    label: 'Tidak Berubah',
    borderColor: 'border-gray-200',
    badgeColor: 'text-gray-500 italic text-[10px]',
    accentColor: '',
  },
  modified: {
    label: 'Diubah',
    borderColor: 'border-amber-300',
    badgeColor: 'text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-[10px] font-medium',
    accentColor: 'border-l-4 border-l-amber-400',
  },
  inserted: {
    label: 'Disisipkan',
    borderColor: 'border-green-300',
    badgeColor: 'text-green-700 bg-green-50 px-2 py-0.5 rounded text-[10px] font-medium',
    accentColor: 'border-l-4 border-l-green-400',
  },
  deleted: {
    label: 'Dihapus',
    borderColor: 'border-red-300',
    badgeColor: 'text-red-700 bg-red-50 px-2 py-0.5 rounded text-[10px] font-medium',
    accentColor: 'border-l-4 border-l-red-400',
  },
};

interface ArticleHeaderProps {
  article: ConsolidationArticle;
  isMatch: boolean;
  isCurrentFocus: boolean;
  isTranslated: boolean;
  hasFullRevocation: boolean;
  fullRevocations: ConsolidationArticleResponse['partialRevocations'];
}

export function ConsolidationArticleHeader({
  article,
  isMatch,
  isCurrentFocus,
  isTranslated,
  hasFullRevocation,
  fullRevocations,
}: ArticleHeaderProps) {
  const config = changeTypeConfig[article.changeType] || changeTypeConfig.unchanged;

  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-lg font-bold capitalize flex items-center gap-2 text-gray-700">
        Pasal {article.articleNumber}
        {!hasFullRevocation && article.changeType !== 'unchanged' && (
          <span className={config.badgeColor}>{config.label}</span>
        )}
        {hasFullRevocation && (
          <span className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded text-[10px] font-medium border border-orange-300">
            Dicabut
          </span>
        )}
        {isMatch && (
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ml-2 ${
              isCurrentFocus ? 'bg-blue-600 text-white' : 'bg-black text-white'
            }`}
          >
            {isCurrentFocus ? 'DITEMUKAN' : 'Cocok'}
          </span>
        )}
        {isTranslated && (
          <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold bg-green-600 text-white">
            EN
          </span>
        )}
      </h3>

      {article.lastChange && article.changeType !== 'unchanged' && !isTranslated && (
        <div className="flex items-start gap-1.5 text-[11px] text-gray-500 mt-1">
          <div className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
          <span className="leading-snug">
            <span className="italic mr-1">
              {article.changeType === 'deleted' && 'Dihapus oleh:'}
              {article.changeType === 'inserted' && 'Disisipkan oleh:'}
              {article.changeType === 'modified' &&
                (article.lastChange.kind === 'penalty-adjustment' ? 'Ancaman pidana disesuaikan oleh:' : 'Diubah oleh:')}
            </span>
            <Link
              href={`/regulation/indonesia/${article.lastChange.regulationId}`}
              className="font-medium text-blue-600 hover:underline transition-colors"
            >
              {article.lastChange.regulationTitle}
            </Link>
          </span>
        </div>
      )}

      {hasFullRevocation && fullRevocations?.[0] && !isTranslated && (
        <div className="flex items-start gap-1.5 text-[11px] text-orange-700 mt-1">
          <div className="w-1 h-1 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />
          <span className="leading-snug">
            <span className="italic mr-1">Dicabut oleh:</span>
            <Link
              href={`/regulation/indonesia/${fullRevocations[0].revokingRegulationId}`}
              className="font-medium hover:underline transition-colors"
            >
              {fullRevocations[0].revokingRegulationTitle}
            </Link>
          </span>
        </div>
      )}
    </div>
  );
}

interface ArticleContentProps {
  article: ConsolidationArticle;
  displayContent: string;
  isTranslated: boolean;
  consolidationId: string;
  isMatch: boolean;
  query: string;
  citingRegulations: CitingRegulation[];
  hasPremiumRole: boolean;
}

export function ConsolidationArticleContent({
  article,
  displayContent,
  isTranslated,
  consolidationId,
  isMatch,
  query,
  citingRegulations,
  hasPremiumRole,
}: ArticleContentProps) {
  return (
    <div
      className={cn(
        'prose text-[15px] max-w-none font-normal text-gray-800 leading-[1.8] tracking-tight text-justify',
        article.changeType === 'deleted' && 'text-gray-500 italic bg-red-50 p-3 rounded'
      )}
      style={{ overflowAnchor: 'none' }}
    >
      {article.changeType === 'modified' && article.previousContent && !isTranslated ? (
        <DiffSideBySide
          oldText={article.previousContent}
          newText={article.content}
          regulationId={consolidationId}
          consolidationId={consolidationId}
          query={isMatch ? query : undefined}
          highlightClassName="bg-yellow-200 text-gray-900 border-b-2 border-yellow-400 font-bold px-0.5 rounded-sm transition-colors duration-300"
          citingRegulations={citingRegulations}
          hasPremiumRole={hasPremiumRole}
        />
      ) : (
        <LegalTextFormatter
          text={displayContent}
          query={isMatch ? query : undefined}
          highlightClassName="bg-yellow-200 text-gray-900 border-b-2 border-yellow-400 font-bold px-0.5 rounded-sm transition-colors duration-300"
          enableArticleReferences
          regulationId={consolidationId}
          consolidationId={consolidationId}
          citingRegulations={citingRegulations}
          hasPremiumRole={hasPremiumRole}
        />
      )}
    </div>
  );
}

interface PartialRevocationsProps {
  articleNumber: string;
  partialRevocations: NonNullable<ConsolidationArticleResponse['partialRevocations']>;
}

/**
 * Renders amar putusan points with proper nested list support.
 *
 * Handles numbering formats:
 *   Top-level: 1., 2., 3. …
 *   Sub-level: 1.1., 1.2., 2.1. …
 *   Sub-sub:   1.1.1., …
 *
 * It works by scanning lines for these patterns and building
 * a nested <ol> structure. Text that doesn't match a list
 * marker is rendered as a plain paragraph.
 */
function AmarPointsRenderer({ text }: { text: string }) {
  // Pre-process: insert newlines before inline sub-numbering like
  // "1.1.", "1.2." that may appear mid-sentence, so the line-split below
  // can pick them up as separate items.
  const normalized = text.replace(/(\d+\.\d+)\.\s+/g, "\n$1. ").trim();

  const lines = normalized.split("\n").map((l) => l.trim()).filter(Boolean);

  // Regex: matches "1.", "1.1.", "1.1.1." at line start and captures the full
  // number prefix (e.g. "1", "1.1") and the rest of the text.
  const markerRx = /^(\d+(?:\.\d+)*)\.\s+(.*)/;

  const topLevel: Array<{ label: string; content: string }> = [];

  for (const line of lines) {
    const m = line.match(markerRx);
    if (m) {
      topLevel.push({ label: m[1], content: m[2] });
    } else {
      // If there's no marker, append to the last item as continuation.
      if (topLevel.length > 0) {
        topLevel[topLevel.length - 1].content += " " + line;
      }
    }
  }

  if (topLevel.length === 0) {
    return <div className="text-sm text-gray-700 leading-relaxed mb-2">{text}</div>;
  }

  return (
    <NestedList items={topLevel} depth={0} />
  );
}

/**
 * Recursively render a flat list of items into nested <ol> elements
 * with CSS counters for hierarchical numbering (1., 1.1., 1.1.1., …).
 *
 * Uses CSS `counter-reset` / `counter-increment` / `counters()` so that
 * nested levels automatically display 1., 1.1., 1.2., … without needing
 * to track the labels manually.
 */
function NestedList({
  items,
  depth,
}: {
  items: Array<{ label: string; content: string }>;
  depth: number;
}) {
  const groups = new Map<string, Array<{ label: string; content: string }>>();

  for (const item of items) {
    const segments = item.label.split(".");
    const key = segments[depth];
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }

  if (groups.size === 0) return null;

  return (
    <ol
      className={`nested-counter-list text-sm text-gray-700 leading-relaxed mb-2 ${depth > 0 ? "ml-6" : "pl-6"}`}
      style={{ listStyle: "none", paddingLeft: depth === 0 ? "1.5em" : undefined }}
    >
      {Array.from(groups.entries()).map(([key, groupItems]) => {
        const exactLabel = groupItems.find(
          (i) => i.label.split(".").length === depth + 1,
        );
        const subItems = groupItems.filter(
          (i) => i.label.split(".").length > depth + 1,
        );

        return (
          <li key={key} className="text-justify mb-1">
            {exactLabel ? <span>{exactLabel.content}</span> : null}
            {subItems.length > 0 && (
              <NestedList items={subItems} depth={depth + 1} />
            )}
          </li>
        );
      })}
      {depth === 0 && (
        <style>{`
          ol.nested-counter-list {
            counter-reset: nested-counter;
          }
          ol.nested-counter-list > li {
            counter-increment: nested-counter;
          }
          ol.nested-counter-list > li::before {
            content: counters(nested-counter, ".") ". ";
            display: inline-block;
            min-width: 1.8em;
            margin-left: -1.5em;
          }
        `}</style>
      )}
    </ol>
  );
}

interface MkRulingsProps {
  articleNumber: string;
  mkRulings: NonNullable<ConsolidationArticleResponse['mkRulings']>;
  consolidationId?: string;
}

/**
 * Renders Constitutional Court (MK) rulings attached to this article.
 * Consistent style with the Penjelasan (explanation) section.
 */
export function ConsolidationMkRulings({
  articleNumber,
  mkRulings,
}: MkRulingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (mkRulings.length === 0) return null;

  return (
    <div className="mt-4 border-t-2 border-gray-100 pt-4">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="flex items-center justify-between w-full text-left group cursor-pointer"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 group-hover:text-black transition-colors">
          <FileText size={16} className="text-gray-500 group-hover:text-gray-700" />
          <span>Putusan MK</span>
          <span className="text-xs px-2 py-0.5 rounded text-blue-700 bg-blue-50">
            Terkait Pasal {articleNumber}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">
            {mkRulings.length} putusan
          </span>
          {isExpanded ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </div>
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isExpanded ? 'max-h-[5000px] opacity-100 mt-4' : 'max-h-0 opacity-0',
        )}
      >
        <div className="space-y-3">
          {mkRulings.map((ruling, idx) => (
            <div
              key={idx}
              className="bg-gray-50 border border-gray-200 p-4 rounded-md"
            >
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Link
                  href={`/id/court-case/constitutional-court/${ruling.regulationConstitutionCourtId}`}
                  className="text-sm font-bold text-gray-800 hover:text-blue-600 hover:underline leading-snug transition-colors"
                >
                  {ruling.decisionNumber}
                </Link>
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded font-medium',
                    ruling.decisionStatusRaw.toLowerCase().includes('sebagian')
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
                  )}
                >
                  {ruling.decisionStatusRaw}
                </span>
              </div>

              {ruling.amarPoints && <AmarPointsRenderer text={ruling.amarPoints} />}

              <div className="flex items-center gap-3 text-[11px] text-gray-500">
                {ruling.decisionDate && (
                  <span>
                    {new Date(ruling.decisionDate).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                )}
                {ruling.pdfUrl && (
                  <a
                    href={ruling.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <ExternalLink className="h-3 w-3" />
                    PDF
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ConsolidationPartialRevocations({
  articleNumber,
  partialRevocations,
}: PartialRevocationsProps) {
  if (partialRevocations.length === 0) return null;

  return (
    <div className="mt-6 pt-3 border-t border-orange-200 bg-orange-50/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <span className="text-[11px] font-bold text-orange-800 uppercase tracking-wide">
          Pasal {articleNumber} Dicabut Sebagian
        </span>
      </div>
      <div className="space-y-2">
        {partialRevocations.map((pr, idx) => (
          <div
            key={idx}
            className="text-[11px] text-orange-800 bg-white px-3 py-2 rounded-md border border-orange-200 shadow-sm"
          >
            <div className="font-medium mb-1">
              {pr.verses && pr.verses.length > 0 && (
                <span>Ayat {pr.verses.join(', ')} dicabut</span>
              )}
              {pr.verses && pr.verses.length > 0 && pr.provision && ' dan '}
              {pr.provision && (
                <span>Ketentuan mengenai &quot;{pr.provision}&quot; dicabut</span>
              )}
              {' oleh:'}
            </div>
            <div className="flex items-start gap-2">
              <Link
                href={`/regulation/indonesia/${pr.revokingRegulationId}`}
                className="font-semibold text-orange-700 hover:underline flex-1"
              >
                {pr.revokingRegulationTitle}
              </Link>
              <span className="text-orange-600/70 text-[10px] flex-shrink-0">
                ({new Date(pr.enactedDate).toLocaleDateString('id-ID')})
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
